// Renders the accordion params panel.
// Emits change events with the updated config.

import { deepMerge } from '../config.js';

const SCHEMA = [
  { section: 'World', fields: [
    { path: 'world.userCount', label: 'User count', type: 'int', min: 1, max: 100000 },
    { path: 'world.returnProb', label: 'Return prob', type: 'float', step: 0.05, min: 0, max: 1, mode: 'simulated' },
    { path: 'world.diurnalAmplitude', label: 'Diurnal amp', type: 'float', step: 0.05, min: 0, max: 1, mode: 'simulated' },
    { path: 'tickMs', label: 'Tick (ms sim)', type: 'int', min: 10, max: 60000 },
  ]},
  { section: 'Assets', fields: [
    { path: 'assets.count', label: 'Count', type: 'int', min: 1, max: 32 },
    { path: 'assets.sizeLognormalMu', label: 'log(size) μ', type: 'float', step: 0.1 },
    { path: 'assets.sizeLognormalSigma', label: 'log(size) σ', type: 'float', step: 0.05 },
    { path: 'assets.churnMin', label: 'Churn min', type: 'float', step: 0.01, min: 0, max: 1 },
    { path: 'assets.churnMax', label: 'Churn max', type: 'float', step: 0.01, min: 0, max: 1 },
    { path: 'assets.modIntervalMeanS', label: 'Mod interval (s)', type: 'int', min: 10, mode: 'simulated' },
    { path: 'assets.modIntervalJitter', label: 'Mod jitter', type: 'float', step: 0.05, min: 0, max: 1, mode: 'simulated' },
    { path: 'assets.staticCompressionRatio', label: 'Static comp ratio', type: 'float', step: 0.02, min: 0.05, max: 1 },
  ]},
  { section: 'Traffic', mode: 'simulated', fields: [
    { path: 'traffic.model', label: 'Model', type: 'select', options: ['poisson', 'zipf', 'trace'] },
    { path: 'traffic.poisson.perAssetLambdaPerSec', label: 'Poisson λ/asset', type: 'float', step: 0.05 },
    { path: 'traffic.zipf.s', label: 'Zipf s', type: 'float', step: 0.05 },
    { path: 'traffic.zipf.totalLambdaPerSec', label: 'Zipf total λ', type: 'float', step: 0.1 },
    { path: 'traffic.trace.json', label: 'Trace JSON', type: 'textarea' },
  ]},
  { section: 'Delta', fields: [
    { path: 'delta.alpha', label: 'α (distance decay)', type: 'float', step: 0.05 },
    { path: 'delta.overheadBytes', label: 'Overhead bytes', type: 'int' },
    { path: 'delta.minRatio', label: 'Min ratio', type: 'float', step: 0.005 },
    { path: 'delta.maxRatio', label: 'Max ratio', type: 'float', step: 0.02 },
  ]},
  { section: 'Server cache', fields: [
    { path: 'server.unifiedPool', label: 'Unified pool', type: 'bool' },
    { path: 'server.capacityBytes', label: 'Unified capacity', type: 'int' },
    { path: 'server.capacityBytesByKind.asset', label: 'Cap assets', type: 'int' },
    { path: 'server.capacityBytesByKind.dictionary', label: 'Cap dicts', type: 'int' },
    { path: 'server.capacityBytesByKind.delta', label: 'Cap deltas', type: 'int' },
    { path: 'server.ttlSec.asset', label: 'TTL asset (s)', type: 'int' },
    { path: 'server.ttlSec.dictionary', label: 'TTL dict (s)', type: 'int' },
    { path: 'server.ttlSec.delta', label: 'TTL delta (s)', type: 'int' },
    { path: 'server.pinHitThreshold', label: 'Pin threshold', type: 'int' },
    { path: 'server.pinWindowSec', label: 'Pin window (s)', type: 'int' },
    { path: 'server.deltaRegenCostMs', label: 'Regen cost (ms)', type: 'int' },
  ]},
  { section: 'Client cache', fields: [
    { path: 'client.perUserCacheBytes', label: 'Per-user bytes', type: 'int' },
    { path: 'client.maxAgeSec.asset', label: 'max-age asset (s)', type: 'int' },
    { path: 'client.maxAgeSec.dictionary', label: 'max-age dict (s)', type: 'int' },
    { path: 'client.ttlSec.asset', label: 'TTL asset (s)', type: 'int' },
    { path: 'client.ttlSec.dictionary', label: 'TTL dict (s)', type: 'int' },
    { path: 'client.revalidationBytesOverhead', label: '304 bytes (approx)', type: 'int' },
  ]},
  { section: 'Failures', fields: [
    { path: 'failures.forceHashMismatchProb', label: 'Force hash miss', type: 'float', step: 0.01, min: 0, max: 1 },
    { path: 'failures.forceUnsupportedEncodingProb', label: 'Force no-dcb', type: 'float', step: 0.01, min: 0, max: 1 },
  ]},
];

export function renderControls(container, config, onChange) {
  container.innerHTML = '';
  for (const s of SCHEMA) {
    const det = document.createElement('details');
    det.open = s.section === 'World' || s.section === 'Assets' || s.section === 'Traffic';
    if (s.mode) det.dataset.mode = s.mode;
    const sum = document.createElement('summary'); sum.textContent = s.section;
    det.appendChild(sum);
    for (const f of s.fields) {
      const wrap = document.createElement('div'); wrap.className = 'field' + (f.type === 'textarea' ? ' wide' : '');
      if (f.mode) wrap.dataset.mode = f.mode;
      const lbl = document.createElement('label'); lbl.textContent = f.label; wrap.appendChild(lbl);
      const input = mkInput(f, get(config, f.path));
      input.addEventListener('change', () => {
        const v = readInput(f, input);
        const patch = setPath({}, f.path, v);
        onChange(deepMerge(config, patch));
      });
      wrap.appendChild(input);
      det.appendChild(wrap);
    }
    container.appendChild(det);
  }
}

function mkInput(f, value) {
  if (f.type === 'textarea') {
    const t = document.createElement('textarea'); t.rows = 4; t.value = value ?? ''; return t;
  }
  if (f.type === 'select') {
    const s = document.createElement('select');
    for (const o of f.options) {
      const opt = document.createElement('option'); opt.value = o; opt.textContent = o;
      if (o === value) opt.selected = true;
      s.appendChild(opt);
    }
    return s;
  }
  if (f.type === 'bool') {
    const i = document.createElement('input'); i.type = 'checkbox'; i.checked = !!value; return i;
  }
  const i = document.createElement('input');
  i.type = 'number';
  if (f.step) i.step = f.step;
  if (f.min != null) i.min = f.min;
  if (f.max != null) i.max = f.max;
  i.value = value ?? 0;
  return i;
}

function readInput(f, el) {
  if (f.type === 'bool') return el.checked;
  if (f.type === 'select' || f.type === 'textarea') return el.value;
  if (f.type === 'int') return parseInt(el.value, 10);
  return parseFloat(el.value);
}

function get(obj, path) {
  return path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
}
function setPath(target, path, value) {
  const keys = path.split('.');
  let cur = target;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = cur[keys[i]] ?? {};
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return target;
}
